import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import RoadSign from "@/components/RoadSign";
import { toast } from "sonner";
import { Users, CreditCard, Receipt, Settings2, LayoutGrid, Trash2, Save, Euro } from "lucide-react";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState({});
  const [txs, setTxs] = useState([]);
  const [users, setUsers] = useState([]);
  const [signs, setSigns] = useState([]);
  const [quiz, setQuiz] = useState([]);
  const [newSign, setNewSign] = useState({ country: "FR", type: "speed-limit", value: "", category: "Prescription", name: "", description: "" });
  const [newQ, setNewQ] = useState({ country: "mixed", question: "", options: ["", "", "", ""], correct: 0, explanation: "" });

  const loadAll = () => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/settings").then(({ data }) => setSettings(data)).catch(() => {});
    api.get("/admin/transactions").then(({ data }) => setTxs(data)).catch(() => {});
    api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
    api.get("/signs").then(({ data }) => setSigns(data)).catch(() => {});
    // load all quiz via admin? reuse public quiz (needs sub; admin has it)
    api.get("/quiz").then(({ data }) => setQuiz(data)).catch(() => {});
  };
  useEffect(loadAll, []);

  const saveSettings = async () => {
    try {
      const payload = { ...settings };
      if (payload.paypal_secret === "••••••••") delete payload.paypal_secret;
      await api.put("/admin/settings", payload);
      toast.success("Paramètres enregistrés");
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const addSign = async () => {
    if (!newSign.name) { toast.error("Nom requis"); return; }
    try { await api.post("/admin/signs", newSign); toast.success("Panneau ajouté"); setNewSign({ ...newSign, name: "", description: "", value: "" }); loadAll(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const delSign = async (id) => { await api.delete(`/admin/signs/${id}`); toast.success("Supprimé"); loadAll(); };

  const addQuiz = async () => {
    if (!newQ.question || newQ.options.some((o) => !o)) { toast.error("Question et 4 options requises"); return; }
    try { await api.post("/admin/quiz", newQ); toast.success("Question ajoutée"); setNewQ({ country: "mixed", question: "", options: ["", "", "", ""], correct: 0, explanation: "" }); loadAll(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const delQuiz = async (id) => { await api.delete(`/admin/quiz/${id}`); toast.success("Supprimé"); loadAll(); };

  const set = (k, v) => setSettings({ ...settings, [k]: v });

  return (
    <div className="max-w-7xl mx-auto px-5 py-12 min-h-[70vh]">
      <h1 className="font-serif text-4xl tracking-tighter text-[#2C2A28] mb-2">Administration</h1>
      <p className="text-[#5C5854] mb-8">Pilotez le contenu, les paiements et vos clients.</p>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
        {stats && [
          { icon: Users, l: "Clients", v: stats.users },
          { icon: Receipt, l: "Commandes payées", v: stats.paid_orders },
          { icon: Euro, l: "Revenus", v: `${stats.revenue_eur.toFixed(2)}€` },
          { icon: LayoutGrid, l: "Panneaux", v: stats.signs },
          { icon: CreditCard, l: "Questions quiz", v: stats.quiz },
        ].map((s) => (
          <div key={s.l} className="bg-white rounded-2xl border border-[#E5E0D8] p-5" data-testid={`stat-${s.l}`}>
            <s.icon className="w-5 h-5 text-[#E06D53] mb-3" />
            <div className="font-serif text-3xl text-[#1D4E89]">{s.v}</div>
            <div className="text-xs uppercase tracking-widest text-[#8C857B] mt-1">{s.l}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="settings">
        <TabsList className="bg-white border border-[#E5E0D8] rounded-full p-1 mb-8 flex-wrap h-auto">
          <TabsTrigger value="settings" className="rounded-full data-[state=active]:bg-[#E06D53] data-[state=active]:text-white" data-testid="admin-tab-settings"><Settings2 className="w-4 h-4 mr-1" /> Paiements</TabsTrigger>
          <TabsTrigger value="transactions" className="rounded-full data-[state=active]:bg-[#E06D53] data-[state=active]:text-white" data-testid="admin-tab-transactions">Transactions</TabsTrigger>
          <TabsTrigger value="users" className="rounded-full data-[state=active]:bg-[#E06D53] data-[state=active]:text-white" data-testid="admin-tab-users">Clients</TabsTrigger>
          <TabsTrigger value="content" className="rounded-full data-[state=active]:bg-[#E06D53] data-[state=active]:text-white" data-testid="admin-tab-content">Contenu</TabsTrigger>
        </TabsList>

        {/* SETTINGS */}
        <TabsContent value="settings">
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl border border-[#E5E0D8] p-7">
              <h3 className="text-lg font-medium text-[#2C2A28] mb-4">Informations de la boutique</h3>
              <div className="space-y-4">
                <div><Label className="text-xs uppercase tracking-widest text-[#8C857B]">Nom commercial</Label>
                  <Input value={settings.business_name || ""} onChange={(e) => set("business_name", e.target.value)} className="mt-1 rounded-xl" data-testid="settings-business-name" /></div>
                <div><Label className="text-xs uppercase tracking-widest text-[#8C857B]">Email de support</Label>
                  <Input value={settings.support_email || ""} onChange={(e) => set("support_email", e.target.value)} className="mt-1 rounded-xl" data-testid="settings-support-email" /></div>
              </div>
              <div className="border-t border-[#E5E0D8] my-6" />
              <h3 className="text-lg font-medium text-[#2C2A28] mb-2">Carte bancaire (Stripe)</h3>
              <p className="text-sm text-[#5C5854] mb-3">Compte Stripe : <span className="font-mono text-xs">{settings.stripe_account_id}</span> · mode {settings.stripe_mode}</p>
              <p className="text-xs text-[#8C857B] bg-[#FAF9F6] border border-[#E5E0D8] rounded-xl p-3">Pour recevoir les paiements par carte, réclamez votre compte Stripe via le lien d'onboarding fourni. Une fois validé (KYC), les fonds arrivent sur votre compte bancaire.</p>
            </div>

            <div className="bg-white rounded-2xl border border-[#E5E0D8] p-7">
              <h3 className="text-lg font-medium text-[#2C2A28] mb-4">PayPal</h3>
              <p className="text-sm text-[#5C5854] mb-4">Saisissez vos identifiants API PayPal (depuis developer.paypal.com) pour recevoir les paiements PayPal directement.</p>
              <div className="space-y-4">
                <div><Label className="text-xs uppercase tracking-widest text-[#8C857B]">Client ID</Label>
                  <Input value={settings.paypal_client_id || ""} onChange={(e) => set("paypal_client_id", e.target.value)} className="mt-1 rounded-xl font-mono text-sm" data-testid="settings-paypal-client" placeholder="AXxxxx..." /></div>
                <div><Label className="text-xs uppercase tracking-widest text-[#8C857B]">Secret</Label>
                  <Input value={settings.paypal_secret || ""} onChange={(e) => set("paypal_secret", e.target.value)} className="mt-1 rounded-xl font-mono text-sm" data-testid="settings-paypal-secret" placeholder="ELxxxx..." /></div>
                <div><Label className="text-xs uppercase tracking-widest text-[#8C857B]">Environnement</Label>
                  <Select value={settings.paypal_mode || "sandbox"} onValueChange={(v) => set("paypal_mode", v)}>
                    <SelectTrigger className="mt-1 rounded-xl" data-testid="settings-paypal-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sandbox">Sandbox (test)</SelectItem>
                      <SelectItem value="live">Live (production)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
          <Button onClick={saveSettings} className="rounded-full h-11 px-8 bg-[#E06D53] hover:bg-[#C95B42] text-white mt-6" data-testid="settings-save"><Save className="w-4 h-4 mr-2" /> Enregistrer les paramètres</Button>
        </TabsContent>

        {/* TRANSACTIONS */}
        <TabsContent value="transactions">
          <div className="bg-white rounded-2xl border border-[#E5E0D8] p-2 overflow-x-auto" data-testid="admin-transactions-table">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Date</TableHead><TableHead>Fournisseur</TableHead><TableHead>Formule</TableHead>
                <TableHead>Montant</TableHead><TableHead>Statut</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {txs.map((t, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-sm">{t.created_at ? new Date(t.created_at).toLocaleString("fr-FR") : "—"}</TableCell>
                    <TableCell className="capitalize">{t.provider}</TableCell>
                    <TableCell>{t.plan_id}</TableCell>
                    <TableCell>{(t.amount / 100).toFixed(2)}€</TableCell>
                    <TableCell><span className={`text-xs px-2 py-1 rounded-full ${t.payment_status === "paid" ? "bg-[#EAF4EF] text-[#2D6A4F]" : "bg-[#FBEDE9] text-[#C95B42]"}`}>{t.payment_status}</span></TableCell>
                  </TableRow>
                ))}
                {txs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-[#8C857B] py-8">Aucune transaction</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* USERS */}
        <TabsContent value="users">
          <div className="bg-white rounded-2xl border border-[#E5E0D8] p-2 overflow-x-auto" data-testid="admin-users-table">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Email</TableHead><TableHead>Rôle</TableHead><TableHead>Abonnement</TableHead><TableHead>Inscrit le</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell>{u.email}</TableCell>
                    <TableCell className="capitalize">{u.role}</TableCell>
                    <TableCell>{u.subscription_active ? <span className="text-[#2D6A4F] text-sm">Actif</span> : <span className="text-[#8C857B] text-sm">—</span>}</TableCell>
                    <TableCell className="text-sm">{u.created_at ? new Date(u.created_at).toLocaleDateString("fr-FR") : "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* CONTENT */}
        <TabsContent value="content">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Add sign */}
            <div className="bg-white rounded-2xl border border-[#E5E0D8] p-7">
              <h3 className="text-lg font-medium text-[#2C2A28] mb-4">Ajouter un panneau</h3>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-[#8C857B]">Pays</Label>
                  <Select value={newSign.country} onValueChange={(v) => setNewSign({ ...newSign, country: v })}>
                    <SelectTrigger className="mt-1 rounded-xl" data-testid="newsign-country"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="FR">France</SelectItem><SelectItem value="CH">Suisse</SelectItem><SelectItem value="IT">Italie</SelectItem></SelectContent>
                  </Select></div>
                <div><Label className="text-xs text-[#8C857B]">Type (SVG)</Label>
                  <Select value={newSign.type} onValueChange={(v) => setNewSign({ ...newSign, type: v })}>
                    <SelectTrigger className="mt-1 rounded-xl" data-testid="newsign-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["speed-limit", "end-speed", "stop", "yield", "priority-road", "no-entry", "no-overtaking", "roundabout", "motorway", "children", "danger", "vignette", "ztl"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div><Label className="text-xs text-[#8C857B]">Valeur (ex: 130)</Label>
                  <Input value={newSign.value} onChange={(e) => setNewSign({ ...newSign, value: e.target.value })} className="mt-1 rounded-xl" data-testid="newsign-value" /></div>
                <div><Label className="text-xs text-[#8C857B]">Catégorie</Label>
                  <Input value={newSign.category} onChange={(e) => setNewSign({ ...newSign, category: e.target.value })} className="mt-1 rounded-xl" data-testid="newsign-category" /></div>
                <div className="col-span-2"><Label className="text-xs text-[#8C857B]">Nom</Label>
                  <Input value={newSign.name} onChange={(e) => setNewSign({ ...newSign, name: e.target.value })} className="mt-1 rounded-xl" data-testid="newsign-name" /></div>
                <div className="col-span-2"><Label className="text-xs text-[#8C857B]">Description</Label>
                  <Input value={newSign.description} onChange={(e) => setNewSign({ ...newSign, description: e.target.value })} className="mt-1 rounded-xl" data-testid="newsign-desc" /></div>
              </div>
              <div className="flex items-center gap-4 mt-4">
                <RoadSign type={newSign.type} value={newSign.value} country={newSign.country} size={56} />
                <Button onClick={addSign} className="rounded-full bg-[#1D4E89] hover:bg-[#173d6b] text-white" data-testid="newsign-add">Ajouter le panneau</Button>
              </div>
            </div>

            {/* Add quiz */}
            <div className="bg-white rounded-2xl border border-[#E5E0D8] p-7">
              <h3 className="text-lg font-medium text-[#2C2A28] mb-4">Ajouter une question quiz</h3>
              <div className="space-y-3">
                <div><Label className="text-xs text-[#8C857B]">Pays</Label>
                  <Select value={newQ.country} onValueChange={(v) => setNewQ({ ...newQ, country: v })}>
                    <SelectTrigger className="mt-1 rounded-xl" data-testid="newq-country"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="mixed">Mixte</SelectItem><SelectItem value="FR">France</SelectItem><SelectItem value="CH">Suisse</SelectItem><SelectItem value="IT">Italie</SelectItem></SelectContent>
                  </Select></div>
                <div><Label className="text-xs text-[#8C857B]">Question</Label>
                  <Input value={newQ.question} onChange={(e) => setNewQ({ ...newQ, question: e.target.value })} className="mt-1 rounded-xl" data-testid="newq-question" /></div>
                {newQ.options.map((o, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input type="radio" name="correct" checked={newQ.correct === i} onChange={() => setNewQ({ ...newQ, correct: i })} data-testid={`newq-correct-${i}`} />
                    <Input value={o} onChange={(e) => { const opts = [...newQ.options]; opts[i] = e.target.value; setNewQ({ ...newQ, options: opts }); }} className="rounded-xl" placeholder={`Option ${i + 1}`} data-testid={`newq-opt-${i}`} />
                  </div>
                ))}
                <div><Label className="text-xs text-[#8C857B]">Explication</Label>
                  <Input value={newQ.explanation} onChange={(e) => setNewQ({ ...newQ, explanation: e.target.value })} className="mt-1 rounded-xl" data-testid="newq-explanation" /></div>
              </div>
              <Button onClick={addQuiz} className="rounded-full bg-[#1D4E89] hover:bg-[#173d6b] text-white mt-4" data-testid="newq-add">Ajouter la question</Button>
            </div>
          </div>

          {/* Existing signs */}
          <h3 className="text-lg font-medium text-[#2C2A28] mt-10 mb-4">Panneaux existants ({signs.length})</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {signs.map((s) => (
              <div key={s.id} className="bg-white rounded-2xl border border-[#E5E0D8] p-4 flex flex-col items-center text-center relative" data-testid={`admin-sign-${s.id}`}>
                <button onClick={() => delSign(s.id)} className="absolute top-2 right-2 text-[#D62828] hover:bg-[#FBE9E9] rounded-full p-1.5" data-testid={`del-sign-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                <RoadSign type={s.type} value={s.value} country={s.country} size={56} />
                <span className="text-xs text-[#8C857B] mt-2">{s.country}</span>
                <span className="text-sm font-medium text-[#2C2A28]">{s.name}</span>
              </div>
            ))}
          </div>

          <h3 className="text-lg font-medium text-[#2C2A28] mt-10 mb-4">Questions existantes ({quiz.length})</h3>
          <div className="space-y-2">
            {quiz.map((q) => (
              <div key={q.id} className="bg-white rounded-xl border border-[#E5E0D8] p-4 flex items-center justify-between gap-3" data-testid={`admin-quiz-${q.id}`}>
                <span className="text-sm text-[#2C2A28]"><span className="text-xs text-[#8C857B] mr-2">[{q.country}]</span>{q.question}</span>
                <button onClick={() => delQuiz(q.id)} className="text-[#D62828] hover:bg-[#FBE9E9] rounded-full p-1.5 shrink-0" data-testid={`del-quiz-${q.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
